import { requestCache } from "src/config/requestCache";
import { createNotification } from "src/config/notifications";
import API from "src/config/API";
import droits from "src/config/CodesDroits";
import {
  IUtilisateurFormulaire,
  IValidationReinitPassword,
} from "src/config/types/user";
import { IProfil, IDroit } from "src/config/types/profil";
import { IResponse } from "src/config/types/response";
import { AxiosResponse, AxiosError } from "axios";

interface LoginCredentials {
  username: string;
  password: string;
}

interface LoginResponse extends IResponse {
  success: boolean;
  message?: string;
}

interface APIErrorResponse {
  detail?: string;
}

export interface InfoServerResponse {
  // Define based on your API response structure
  [key: string]: any;
}

class AuthService {
  private api: API;
  private authenticated = false;
  private loggingOut = false;

  constructor() {
    this.api = new API();
  }

  async setDroitsInStorage(): Promise<void> {
    const resDroits = await this.getDroitsCurrentUserFromApi();
    if (resDroits && resDroits.status === 200 && resDroits.data) {
      localStorage.setItem("droits", JSON.stringify(resDroits.data));
    }
  }

  async setProfilsInStorage(): Promise<void> {
    const resProfils = await this.getProfilsUserFromApi();
    if (resProfils && resProfils.status === 200 && resProfils.data) {
      localStorage.setItem("profils", JSON.stringify(resProfils.data));
    }
  }

  async login(user: LoginCredentials): Promise<LoginResponse> {
    try {
      this.loadAPI();
      await this.initializeCsrfToken();
      const params = new URLSearchParams();
      params.append("username", user.username);
      params.append("password", user.password);
      const res = await this.api.post<{ success: boolean; csrf_token: string }>(
        "/auth/login",
        params,
        { headers: { "Content-Type": "application/x-www-form-urlencoded" } },
      );
      if (res.data.success) {
        API.setCsrfToken(res.data.csrf_token);
        this.authenticated = true;
        localStorage.removeItem("hideMentionsLegales");

        await this.setDroitsInStorage();
        await this.setProfilsInStorage();

        return { success: true };
      }

      return {
        success: false,
      };
    } catch (e) {
      const error = e as AxiosError<APIErrorResponse>;
      return {
        success: false,
        message: error.response?.data?.detail,
      };
    }
  }

  isLoggedIn(): boolean {
    return this.authenticated;
  }

  async checkSession(): Promise<boolean> {
    localStorage.removeItem("user");
    try {
      this.loadAPI();
      await this.initializeCsrfToken();
      const res = await this.api.get<{
        authenticated: boolean;
        csrf_token: string;
      }>("/auth/session");
      this.authenticated = res.data.authenticated;
      API.setCsrfToken(res.data.csrf_token);
      if (!this.authenticated) {
        this.clearSessionCaches();
      }
      return this.authenticated;
    } catch (error) {
      const apiError = error as AxiosError;
      if (apiError.response?.status === 401) {
        this.authenticated = false;
        API.setCsrfToken(null);
        this.clearSessionCaches();
        return false;
      }
      throw error;
    }
  }

  private async initializeCsrfToken(): Promise<void> {
    const res = await this.api.get<{ csrf_token: string }>("/auth/csrf");
    API.setCsrfToken(res.data.csrf_token);
  }

  clearSessionCaches(): void {
    requestCache.clear();
    localStorage.removeItem("user");
    localStorage.removeItem("droits");
    localStorage.removeItem("profils");
  }

  async createUserCodeReinitPassword(username: string): Promise<any> {
    await this.loadAPI();
    const res = await this.api.post(
      `/utilisateur/reinitpassword?username=${encodeURI(username)}`,
    );
    if (res.status === 200) {
      return res.data;
    }
  }

  async validateUserCodeReinitPassword(
    data: IValidationReinitPassword,
  ): Promise<any> {
    await this.loadAPI();
    const res = await this.api.post(
      "/utilisateur/validatereinitpassword",
      data,
    );
    if (res.status === 200) {
      return res.data;
    }
  }

  getQueryParam(item: string): string | null {
    return new URLSearchParams(window.location.search).get(item);
  }

  async logout(doRedirectUri: boolean = true): Promise<void> {
    if (this.loggingOut) {
      return;
    }
    this.loggingOut = true;
    const redirect_uri = encodeURIComponent(
      window.location.pathname + window.location.search,
    );
    try {
      this.loadAPI();
      await this.api.post("/auth/logout");
    } catch (error) {
      console.error("Impossible de fermer la session côté serveur", error);
    } finally {
      this.authenticated = false;
      API.setCsrfToken(null);
      this.clearSessionCaches();
      window.location.replace(
        doRedirectUri
          ? `/auth/login?redirect_uri=${redirect_uri}&loggedout=true`
          : "/auth/login?loggedout=true",
      );
    }
  }

  async createFormulaireUtilisateur(
    formulaire: IUtilisateurFormulaire,
  ): Promise<AxiosResponse | undefined> {
    try {
      await this.loadAPI();
      const res = await this.api.post("/utilisateur/formulaire", formulaire);
      return res;
    } catch (e) {
      const error = e as AxiosError;
      return error.response;
    }
  }

  loadAPI(): void {
    this.api.createAPI();
  }

  async getInfosServer(): Promise<AxiosResponse<InfoServerResponse>> {
    await this.loadAPI();
    const res = await this.api.get<InfoServerResponse>("/info");
    return res;
  }

  async getProfilsUserFromApi(): Promise<AxiosResponse<IProfil[]> | undefined> {
    try {
      const res = await this.api.get<IProfil[]>("/profil/utilisateur");
      return res;
    } catch (e) {
      const error = e as AxiosError<APIErrorResponse>;
      createNotification(
        "error",
        "Erreur",
        error.response?.data?.detail || "Erreur inconnue",
      );
    }
  }

  async getDroitsCurrentUserFromApi(): Promise<
    AxiosResponse<IDroit[]> | undefined
  > {
    try {
      const res = await this.api.get<IDroit[]>("/profil/droits/utilisateur");
      return res;
    } catch (e) {
      const error = e as AxiosError<APIErrorResponse>;
      createNotification(
        "error",
        "Erreur",
        error.response?.data?.detail || "Erreur inconnue",
      );
    }
  }

  getCurrentDroits(): IDroit[] | null {
    const droitsStr = localStorage.getItem("droits");
    return droitsStr ? JSON.parse(droitsStr) : null;
  }

  hasDroit(codetypedroit: string, modalite: number): boolean {
    const droits = this.getCurrentDroits();
    if (droits == null) {
      return false;
    }
    let res = false;
    droits.forEach((droit) => {
      if (
        droit.codetypedroit === codetypedroit &&
        droit.iddroitmodalite >= modalite
      ) {
        res = true;
      }
    });
    return res;
  }

  hasProfil(codeprofil: string): boolean {
    const profilsStr = localStorage.getItem("profils");
    if (
      profilsStr == null ||
      Object.keys(JSON.parse(profilsStr)).length === 0
    ) {
      return false;
    }
    const profils: IProfil[] = JSON.parse(profilsStr);
    return profils.some((profil) => profil.codeprofil === codeprofil);
  }

  hasOrganismeStockeur(idorganismestockeur: number): boolean {
    const profilsStr = localStorage.getItem("profils");
    if (profilsStr == null) {
      return false;
    }
    const profils: IProfil[] = JSON.parse(profilsStr);
    return profils.some(
      (profil) => profil.idorganismestockeur === idorganismestockeur,
    );
  }

  isOrganismeStockeur(): boolean {
    const profilsStr = localStorage.getItem("profils");
    if (profilsStr == null) {
      return false;
    }
    const profils: IProfil[] = JSON.parse(profilsStr);
    return profils.some((profil) => profil.idorganismestockeur != null);
  }

  isAPGMB(): boolean {
    const profilsStr = localStorage.getItem("profils");
    if (profilsStr == null) {
      return false;
    }
    const profils: IProfil[] = JSON.parse(profilsStr);
    return profils.some((profil) =>
      [
        droits.profils.ADMINISTRATEUR,
        droits.profils.RESPO_ADMIN,
        droits.profils.CONSEILLER,
      ].includes(profil.codeprofil),
    );
  }
}

export default new AuthService();
